import {
  Organization,
  OrganizationUser,
  Relationship,
} from "@cohesive/api-client";
import { TooltipPortal } from "@radix-ui/react-tooltip";
import { Lock } from "lucide-react";
import React from "react";
import { titleCase } from "title-case";
import { MetadataIcon } from "../../types";
import { ResourceIcon } from "../ResourcePill";
import { Separator } from "../ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

export interface Group {
  id: string;
  organization_id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface RelationshipWithResource extends Relationship {
  resource?: Group | OrganizationUser | Organization;
}

export interface UserAvatarListProps {
  relationships: RelationshipWithResource[];
  maxVisible?: number;
  /** Plain user UUIDs currently active on the canvas — drives the green online dot */
  presentUserIds?: string[];
}

const isOrganization = (
  resource: RelationshipWithResource["resource"],
): resource is Organization => {
  return typeof resource === "object" && resource && "logo_url" in resource;
};

const isOrganizationUser = (
  resource: RelationshipWithResource["resource"],
): resource is OrganizationUser => {
  return typeof resource === "object" && resource && "picture_url" in resource;
};

const getResourceIcon = (
  item: RelationshipWithResource,
): MetadataIcon | undefined => {
  if (isOrganizationUser(item.resource) && item.resource.picture_url) {
    return {
      type: "custom",
      custom: {
        url: item.resource.picture_url,
      },
    };
  } else if (isOrganization(item.resource) && item.resource.logo_url) {
    return {
      type: "custom",
      custom: {
        url: item.resource.logo_url,
      },
    };
  } else if (
    item?.resource &&
    "icon" in item?.resource &&
    (item?.resource?.icon as MetadataIcon)
  ) {
    return item.resource.icon as MetadataIcon;
  }
  return undefined;
};

export const UserAvatarList: React.FC<UserAvatarListProps> = ({
  relationships,
  maxVisible = 5,
  presentUserIds,
}) => {
  const remaining = relationships.length - maxVisible;

  return (
    <div className="flex -space-x-2">
      {relationships
        ?.sort((a, b) => {
          if (a.relation === "owner") return -1;
          if (b.relation === "owner") return 1;
          return 0;
        })
        .slice(0, maxVisible)
        .map((item, i) => {
          const icon = getResourceIcon(item);
          const isOnlyOwner =
            relationships.length === 1 &&
            item.relation === "owner" &&
            item.subject_object_type === "organization_user";

          const resourceUserId = isOrganizationUser(item.resource)
            ? item.resource.id
            : undefined;
          const isOnline =
            !!resourceUserId && !!presentUserIds?.includes(resourceUserId);

          return (
            <div
              key={`${item.subject_object_type}-${item.subject_object_id}-${i}`}
              className="relative"
              style={{
                zIndex: maxVisible - relationships.indexOf(item),
              }}
            >
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <ResourceIcon
                        type={item.subject_object_type}
                        icon={icon}
                        className="flex h-8 w-8 items-center justify-center rounded-full overflow-hidden border-2 border-background"
                      />
                      {isOnlyOwner && (
                        <div className="bg-background p-1 border absolute -bottom-1 -right-1 rounded-full">
                          <Lock className="h-2 w-2 text-muted-foreground" />
                        </div>
                      )}
                      {isOnline && (
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-lc-active border-2 border-background" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipPortal>
                    <TooltipContent>
                      <div>
                        <div>{item?.resource?.name}</div>
                        {isOrganization(item.resource) ? (
                          <div>
                            {item.subject_relation === "member" ? (
                              <span className="text-xs text-muted-foreground truncate">
                                All members in your organization
                              </span>
                            ) : item.subject_relation === "admin" ? (
                              <span className="text-xs text-muted-foreground truncate">
                                All admins in your organization
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground truncate">
                            {isOnlyOwner
                              ? "Private"
                              : isOnline
                                ? `${titleCase(item.relation)} · Online`
                                : titleCase(item.relation)}
                          </span>
                        )}
                      </div>
                    </TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        })}
      {remaining > 0 && (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <div
                className="flex items-center justify-center w-8 h-8 rounded-full bg-neutral-100 border-2 border-background text-xs font-medium text-neutral-600"
                style={{ zIndex: 0 }}
              >
                +{remaining}
              </div>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent>
                <div className="flex flex-col gap-2">
                  {relationships.slice(maxVisible).map((item, i) => (
                    <>
                      <div
                        key={`${item.subject_object_type}-${item.subject_object_id}-${i}`}
                        className="flex items-center gap-3"
                      >
                        <ResourceIcon
                          type={item.subject_object_type}
                          icon={getResourceIcon(item)}
                          className="rounded-full"
                          size={35}
                        />
                        <div>
                          <div>{item?.resource?.name}</div>
                          {isOrganization(item.resource) ? (
                            <div>
                              {item.subject_relation === "member" ? (
                                <span className="text-xs text-muted-foreground truncate leading-4">
                                  All members in your organization
                                </span>
                              ) : item.subject_relation === "admin" ? (
                                <span className="text-xs text-muted-foreground truncate leading-4">
                                  All admins in your organization
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground truncate leading-4">
                              {titleCase(item.relation)}
                            </span>
                          )}
                        </div>
                      </div>
                      {i < relationships.length - 2 && <Separator />}
                    </>
                  ))}
                </div>
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
